import React, { useMemo, useState } from 'react';
import { User, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface Squad {
  id: string;
  name: string;
  color: string;
}

interface Sdr {
  user_id: string;
  sales_name: string | null;
  email: string | null;
  squad_id: string | null;
}

interface Schedule {
  sdr_id: string | null;
  date: string;
}

interface SdrAvailabilityPickerProps {
  sdrs: Sdr[];
  squads: Squad[];
  schedules: Schedule[];
  selectedSdrId?: string;
  selectedSdrIds?: string[];
  selectedDates: string[];
  onSdrSelect?: (sdrId: string) => void;
  onSdrMultiSelect?: (sdrIds: string[]) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  multiSelect?: boolean;
}

const SdrAvailabilityPicker: React.FC<SdrAvailabilityPickerProps> = ({
  sdrs,
  squads,
  schedules,
  selectedSdrId = '',
  selectedSdrIds = [],
  selectedDates,
  onSdrSelect,
  onSdrMultiSelect,
  disabled = false,
  label = 'SDR',
  required = false,
  multiSelect = false
}) => {
  const [selectedSquadFilter, setSelectedSquadFilter] = useState<string>('all');

  const handleSdrClick = (sdrId: string) => {
    if (disabled) return;

    if (multiSelect && onSdrMultiSelect) {
      const newSelection = selectedSdrIds.includes(sdrId)
        ? selectedSdrIds.filter(id => id !== sdrId)
        : [...selectedSdrIds, sdrId];
      onSdrMultiSelect(newSelection);
    } else if (onSdrSelect) {
      onSdrSelect(sdrId);
    }
  };

  const getSdrWorkload = (sdrId: string) => {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0);

    return schedules.filter(s => {
      if (s.sdr_id !== sdrId) return false;
      const scheduleDate = new Date(s.date);
      return scheduleDate >= startOfMonth && scheduleDate <= endOfMonth;
    }).length;
  };

  const getSdrConflicts = (sdrId: string) => {
    return selectedDates.filter(date => {
      return schedules.some(s => s.sdr_id === sdrId && s.date === date);
    });
  };

  const getAvailabilityStatus = (sdrId: string) => {
    const conflicts = getSdrConflicts(sdrId);
    if (conflicts.length > 0) return 'conflict';

    const workload = getSdrWorkload(sdrId);
    if (workload >= 20) return 'overloaded';
    if (workload >= 15) return 'medium';
    return 'available';
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'conflict':
        return {
          icon: XCircle,
          color: 'text-red-400',
          bg: 'bg-red-500/20',
          border: 'border-red-500',
          label: 'Conflit'
        };
      case 'overloaded':
        return {
          icon: AlertCircle,
          color: 'text-orange-400',
          bg: 'bg-orange-500/20',
          border: 'border-orange-500',
          label: 'Chargé'
        };
      case 'medium':
        return {
          icon: AlertCircle,
          color: 'text-yellow-400',
          bg: 'bg-yellow-500/20',
          border: 'border-yellow-500',
          label: 'Moyen'
        };
      default:
        return {
          icon: CheckCircle2,
          color: 'text-green-400',
          bg: 'bg-green-500/20',
          border: 'border-green-500',
          label: 'Disponible'
        };
    }
  };

  const filteredSdrs = useMemo(() => {
    let filtered = [...sdrs];

    if (selectedSquadFilter !== 'all') {
      filtered = filtered.filter(sdr => sdr.squad_id === selectedSquadFilter);
    }

    filtered.sort((a, b) => {
      const statusA = getAvailabilityStatus(a.user_id);
      const statusB = getAvailabilityStatus(b.user_id);
      const priority = { available: 0, medium: 1, overloaded: 2, conflict: 3 };
      return (priority[statusA] || 0) - (priority[statusB] || 0);
    });

    return filtered;
  }, [sdrs, selectedSquadFilter, selectedDates, schedules]);

  const uniqueSquads = useMemo(() => {
    const squadIds = new Set(sdrs.map(s => s.squad_id).filter(Boolean));
    return squads.filter(sq => squadIds.has(sq.id));
  }, [sdrs, squads]);

  const isSelected = (sdrId: string) => {
    if (multiSelect) {
      return selectedSdrIds.includes(sdrId);
    }
    return selectedSdrId === sdrId;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-300">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        {multiSelect && (
          <span className="text-xs text-gray-400">
            {selectedSdrIds.length} sélectionné{selectedSdrIds.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {uniqueSquads.length > 1 && (
        <div className="flex items-center space-x-2 mb-3">
          <button
            onClick={() => setSelectedSquadFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              selectedSquadFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Tous
          </button>
          {uniqueSquads.map(squad => (
            <button
              key={squad.id}
              onClick={() => setSelectedSquadFilter(squad.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1.5 ${
                selectedSquadFilter === squad.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: squad.color }}
              />
              <span>{squad.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-1">
        {filteredSdrs.map(sdr => {
          const squad = squads.find(sq => sq.id === sdr.squad_id);
          const workload = getSdrWorkload(sdr.user_id);
          const conflicts = getSdrConflicts(sdr.user_id);
          const status = getAvailabilityStatus(sdr.user_id);
          const statusConfig = getStatusConfig(status);
          const sdrIsSelected = isSelected(sdr.user_id);
          const StatusIcon = statusConfig.icon;
          const canSelect = !disabled && (multiSelect || status !== 'conflict');

          return (
            <motion.button
              key={sdr.user_id}
              whileHover={{ scale: canSelect ? 1.02 : 1 }}
              whileTap={{ scale: canSelect ? 0.98 : 1 }}
              onClick={() => handleSdrClick(sdr.user_id)}
              disabled={!canSelect}
              className={`
                relative p-4 rounded-lg border-2 transition-all text-left
                ${sdrIsSelected ? 'bg-blue-600 border-blue-500' : `bg-gray-700 ${statusConfig.border}`}
                ${!canSelect ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-650'}
              `}
            >
              <div className="flex items-start space-x-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: squad?.color || '#3B82F6' }}
                >
                  <span className="text-white text-sm font-semibold">
                    {(sdr.sales_name || sdr.email || 'XX')
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <h4 className="text-sm font-semibold text-white truncate">
                      {sdr.sales_name || sdr.email || 'SDR'}
                    </h4>
                    <StatusIcon className={`h-4 w-4 ${statusConfig.color} flex-shrink-0 ml-2`} />
                  </div>

                  {squad ? (
                    <div className="flex items-center space-x-1.5 mt-1">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: squad.color }}
                      />
                      <span className="text-xs text-gray-400">{squad.name}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 italic mt-1">Sans Squad</div>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      {workload} jours ce mois
                    </span>
                    <span className={`text-xs font-medium ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                  </div>

                  {conflicts.length > 0 && (
                    <div className="mt-2 text-xs text-red-400">
                      {conflicts.length} conflit{conflicts.length > 1 ? 's' : ''} détecté{conflicts.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>

              {sdrIsSelected && (
                <div className="absolute top-2 right-2">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
              )}

              {multiSelect && (
                <div className="absolute top-2 left-2">
                  <input
                    type="checkbox"
                    checked={sdrIsSelected}
                    onChange={() => {}}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 pointer-events-none"
                  />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {filteredSdrs.length === 0 && (
        <div className="text-center py-12 bg-gray-750 rounded-lg border border-gray-600">
          <User className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Aucun SDR disponible pour cette squad</p>
        </div>
      )}
    </div>
  );
};

export default SdrAvailabilityPicker;
